"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const mustache_1 = __importDefault(require("mustache"));
require("../helpers/virtual-select");
/**
  * Filtering dropdowns for listing pages and other components that filter from results
  * @module listingFilters
  * @param {Object} el - The element to attach the filters to
  * @param {Object} options - The options for the filters
  * @param {Array} items - The items to filter
  * @param {Object} filters - The preselected filters
  * @param {String} itemTemplate - The template for the items
  * @param {Array} searchFields - The fields to search from, e.g. ['title', 'description']
  * @param {Number} limit - The number of items to show at once
  * @param {Number} resultsOffset - The offset for the results, For example on Our stories on CPT where one item is displayed as highlight, but 'show more' should only show next [{limit} amount of items
  * @param {String} noResultsTemplate - The template for no results
  * @param {Object} localizations - The localizations for the filters
  * @returns {Object} listingFilters

  *
 */
const listingFilters = {
    el: {
        resultsEl: null,
        showMoreButton: null,
        countEl: null,
        submitButton: null,
        filterEl: null,
    },
    searchDelay: 700,
    minCharacters: 3,
    controller: new AbortController(),
    timer: null,
    items: [],
    filteredItems: [],
    freeTextItems: [],
    itemTemplate: '',
    filters: {},
    searchValue: '',
    searchFields: [],
    options: {},
    limit: 10,
    resultsOffset: 0,
    page: 1,
    noResultsTemplate: '',
    localizations: {
        option: 'option',
        options: 'options',
        count: '%count% results',
    },
    searchMode: 'browser',
    /**
     * Initialises the functionality.
     * Attaches event handlers to toggle elements
     */
    initListingFilters(el) {
        console.log('listingFilters init');
        this.el = {
            resultsEl: document.getElementById('listing-results'),
            showMoreButton: document.getElementById('listing-show-more'),
            countEl: document.getElementById('section-listing-count'),
            submitButton: document.getElementById('listing-search-submit'),
            filterEl: document.getElementById('listing-filters'),
        };
        const config = window.listingConfig;
        if (config) {
            this.options = config.options;
            this.items = config.items;
            if (config.preSelections) {
                this.filters = config.preSelections;
            }
            this.filteredItems = config.filteredItems || config.items;
            this.itemTemplate = config.itemTemplate || '';
            this.searchFields = config.searchFields || [];
            this.limit = config.limit || 10;
            this.resultsOffset = config.resultsOffset || 0;
            this.noResultsTemplate = config.noResultsTemplate || '';
            if (config.localizations) {
                this.localizations = config.localizations;
            }
            if (config.resultsEl) {
                this.el.resultsEl = document.getElementById(config.resultsEl);
            }
            this.searchMode = config.searchMode || 'browser';
        }
        this.addEventListeners(el);
    },
    renderResults(page = 1) {
        const resultsEl = this.el.resultsEl;
        if (resultsEl) {
            let resultsHtml = '';
            // Clear and reset results for the first page
            if (page === 1) {
                this.page = 1;
                this.filteredItems.slice(0, this.limit).forEach((item) => {
                    resultsHtml = resultsHtml + mustache_1.default.render(this.itemTemplate, item);
                });
                resultsEl.innerHTML = resultsHtml;
            }
            else {
                const lastChild = resultsEl.lastElementChild;
                lastChild?.insertAdjacentHTML('afterend', this.filteredItems
                    .slice((page - 1) * this.limit + this.resultsOffset, this.limit * page + this.resultsOffset)
                    .map((item) => mustache_1.default.render(this.itemTemplate, item))
                    .join(''));
            }
            // Adjust the visibility of 'Show more' button
            if (this.filteredItems.length >
                this.limit * this.page + this.resultsOffset) {
                this.el.showMoreButton?.classList.remove('hidden');
            }
            else {
                this.el.showMoreButton?.classList.add('hidden');
            }
            // Handle no results
            if (this.filteredItems.length === 0) {
                console.log('no results');
                resultsEl.innerHTML = '' + mustache_1.default.render(this.noResultsTemplate, {});
            }
            // Update the count display
            if (this.el.countEl) {
                this.el.countEl.innerHTML = this.localizations.count.replace('%count%', this.filteredItems.length);
            }
            // Remove loading classes
            this.el.resultsEl?.classList.remove('loading');
            this.el.filterEl?.classList.remove('loading');
        }
    },
    addEventListeners(el) {
        const selects = el.querySelectorAll('.listing-filters-select');
        const self = this;
        selects.forEach((select) => {
            if (this.options[select.dataset.name]) {
                window.VirtualSelect.init({
                    optionHeight: 48,
                    alwaysShowSelectedOptionsCount: true,
                    optionSelectedText: this.localizations.option || '',
                    optionsSelectedText: this.localizations.options || '',
                    ele: select,
                    disableSelectAll: true,
                    disableAllOptionsSelectedText: true,
                    silentInitialValueSet: true,
                    selectedValue: this.filters[select.dataset.name],
                    options: this.options[select.dataset.name],
                    showSelectedOptionsFirst: false,
                });
                select.addEventListener('change', function () {
                    if (this.value.length) {
                        self.filters[this.dataset.name] = this.value;
                    }
                    else {
                        delete self.filters[this.dataset.name];
                    }
                    if (self.searchMode === 'browser') {
                        self.filterResults();
                    }
                });
            }
        });
        const searchInput = el.querySelector('.listing-filters-search');
        if (searchInput) {
            // Handle search input events
            let oldValue = searchInput?.value.trim();
            searchInput?.addEventListener('keyup', (e) => {
                const value = searchInput.value.trim() || '';
                // Submit on enter
                if (e.key === 'Enter' || e.keyCode === 13) {
                    if (value.length >= this.minCharacters) {
                        this.searchValue = value;
                    }
                    self.filterResults();
                }
                else {
                    this.controller.abort();
                    this.controller = new AbortController();
                    clearTimeout(this.timer);
                    this.timer = setTimeout(async () => {
                        if (oldValue === value) {
                            return;
                        }
                        // Search value is too short
                        if (value.length < this.minCharacters) {
                            this.searchValue = value;
                            if (this.searchMode === 'browser') {
                                self.filterResults();
                            }
                            return;
                        }
                        oldValue = value;
                        try {
                            this.el.resultsEl?.classList.add('loading');
                            this.searchValue = value;
                            if (this.searchMode === 'browser') {
                                self.filterResults();
                            }
                            return true;
                        }
                        catch (err) {
                            if (err instanceof Error && err.name !== 'AbortError') {
                                console.log(err);
                            }
                        }
                    }, this.searchDelay);
                }
            });
        }
        if (this.el.showMoreButton) {
            this.el.showMoreButton.addEventListener('click', (e) => {
                if (this.filteredItems.length >
                    this.limit * this.page + this.resultsOffset) {
                    this.page++;
                    this.renderResults(this.page);
                }
            });
        }
        if (this.el.submitButton) {
            this.el.submitButton.addEventListener('click', (e) => {
                self.filterResults();
            });
        }
    },
    search(value) {
        try {
            this.el.resultsEl?.classList.add('loading');
            this.searchValue = value;
            this.filterResults(true);
            return true;
        }
        catch (err) {
            if (err instanceof Error && err.name !== 'AbortError') {
                console.log(err);
            }
        }
    },
    filterResults() {
        if (this.searchMode === 'browser') {
            this.resultsOffset = 0;
            const hasFilters = Object.keys(this.filters).length > 0;
            const hasSearch = this.searchValue.length > 2;
            if (hasFilters || hasSearch) {
                if (hasFilters) {
                    this.filteredItems = this.items.filter((item) => {
                        let match = true;
                        for (const key in this.filters) {
                            if (match && this.filters[key].length) {
                                if (item[key] &&
                                    item[key].some((value) => this.filters[key].includes(value))) {
                                    match = true;
                                }
                                else {
                                    match = false;
                                }
                            }
                        }
                        return match;
                    });
                }
                if (hasSearch) {
                    this.filteredItems = (!hasFilters ? this.items : this.filteredItems).filter((item) => {
                        let match = false;
                        for (const key of this.searchFields) {
                            if (item[key].toLowerCase().includes(this.searchValue.toLowerCase())) {
                                match = true;
                            }
                        }
                        return match;
                    });
                }
            }
            else if (!hasSearch && !hasFilters) {
                this.filteredItems = this.items;
            }
        }
        else if (this.searchMode === 'ajax') {
            this.el.filterEl?.classList.add('loading');
            const formData = new FormData();
            for (const key in this.filters) {
                if (this.filters[key]) {
                    formData.append(key, JSON.stringify(this.filters[key]));
                }
            }
            if (this.searchValue.length &&
                this.searchValue.length > this.minCharacters) {
                formData.append('search', this.searchValue);
            }
            try {
                fetch(`/stage-ajax/siteSearch/?stage-language=${document.body.getAttribute('data-language')}&context=releases`, {
                    method: 'POST',
                    body: formData,
                    signal: this.controller.signal,
                })
                    .then((response) => response.json())
                    .then((response) => {
                    this.filteredItems = response.hits;
                    this.renderResults();
                })
                    .catch((err) => {
                    if (err instanceof Error && err.name !== 'AbortError') {
                        console.log('Error fetching releases');
                        console.log(err);
                    }
                    this.el.resultsEl?.classList.remove('loading');
                    this.el.filterEl?.classList.remove('loading');
                });
                return true;
            }
            catch (err) {
                if (err instanceof Error && err.name !== 'AbortError') {
                    console.log(err);
                }
                this.el.resultsEl?.classList.remove('loading');
                this.el.filterEl?.classList.remove('loading');
            }
        }
        this.page = 1;
        this.renderResults(this.page);
    },
};
exports.default = listingFilters;
